/**
 * runModulePaneHydrationEffect — the conversation-change module-pane hydration
 * effect body extracted from ChatBox. It bumps the hydration sequence, seeds
 * pane state synchronously from the URL (or blanks it when there is no
 * conversation), then awaits the persisted pane state and applies it under
 * THREE guards (ADR 028 — each one closes a path that lost user clicks):
 *
 *  1. CONVERSATION-CHANGE ONLY: the effect re-runs when its dep identities
 *     rotate (e.g. `hydrateModulePaneState` is keyed on the auth token), but a
 *     re-run for the SAME conversation must not re-seed — the pane is live and
 *     the user may have moved it off the URL's module (agent-driven focus
 *     never navigates). HOWEVER, React's cleanup cancels any in-flight read
 *     before the re-run (StrictMode does this deterministically on every dev
 *     mount), so a same-conversation re-run ADOPTS an unapplied in-flight
 *     hydration: it re-issues the read under the ORIGINAL intent token —
 *     without re-seeding or re-stamping — instead of silently dropping the
 *     persisted restore.
 *  2. INTENT GUARD: the async apply is dropped if any user/agent intent write
 *     hit the pane authority after the hydration began (`beginHydration` /
 *     `isHydrationCurrent` from ModulePaneContext) — a click landing inside
 *     the persisted-state GET round-trip wins over the (pre-click) result.
 *  3. URL WINS: when the new conversation's URL explicitly names a module
 *     (deep link, sidebar cross-conversation open), the persisted state may
 *     not override the target or visibility — it only restores the anchor when
 *     it describes the same module. Persisted state fully applies only on
 *     URL-less switches (e.g. picking a conversation from history).
 *
 * The legacy guards stay: the per-run `cancelled` flag and the
 * modulePaneHydrationSequenceRef bump (a NEWER conversation switch discards an
 * older in-flight result). Refs and setters are passed BY REFERENCE so
 * reads/writes stay shared with ChatBox; all writes here are background
 * (intent:false) by construction — ChatBox threads the background setters.
 */
import type { MutableRefObject } from 'react';

export interface PendingPaneHydration {
  conversationId: string;
  intentToken: number;
}

export interface ModulePaneHydrationDeps {
  conversationId: string | null;
  isMobile: boolean;
  moduleIdParam: string | null | undefined;
  /**
   * The route's `:view` segment ('chat' for the explicit chat-only URL our
   * conversation-switch nav builds). Used to gate the persisted restore in a
   * StrictMode-safe way — see the restore block below.
   */
  view: string | null | undefined;
  moduleEmbedAnchorMessageId: string | null;
  previousConversationIdRef: MutableRefObject<string | null>;
  moduleEmbedAnchorByConversationRef: MutableRefObject<Record<string, string | null>>;
  modulePaneHydrationSequenceRef: MutableRefObject<number>;
  latestMessageIdRef: MutableRefObject<string | null>;
  /**
   * The not-yet-applied hydration read, if one is in flight (or was cancelled
   * by an effect cleanup before applying). Lets a same-conversation re-run
   * adopt it instead of dropping the persisted restore (Guard 1).
   */
  pendingHydrationRef: MutableRefObject<PendingPaneHydration | null>;
  setChatPaneModuleId: (id: string | null) => void;
  setIsModulePaneVisible: (visible: boolean) => void;
  setMobileView: (view: 'chat' | 'module') => void;
  setModuleEmbedAnchorMessageId: (id: string | null) => void;
  hydrateModulePaneState: (conversationId: string) => Promise<{
    moduleId: string;
    isVisible: boolean;
    isFullscreen: boolean;
    anchorMessageId: string | null;
  } | null>;
  /** ModulePaneContext intent guard (see header). */
  beginHydration: () => number;
  isHydrationCurrent: (token: number) => boolean;
}

/**
 * Start the async persisted-state read for `conversationId` and apply it under
 * the sequence / cancellation / intent / URL-wins guards. Used by both the
 * fresh-conversation run and the Guard-1 adopt re-run. Returns the cleanup
 * that cancels this read.
 */
function startHydrationRead(
  deps: ModulePaneHydrationDeps,
  conversationId: string,
  urlSeededModuleId: string | null,
  hydrationSequence: number,
  intentToken: number,
): () => void {
  const {
    isMobile,
    view,
    moduleEmbedAnchorByConversationRef,
    modulePaneHydrationSequenceRef,
    latestMessageIdRef,
    pendingHydrationRef,
    setChatPaneModuleId,
    setIsModulePaneVisible,
    setModuleEmbedAnchorMessageId,
    hydrateModulePaneState,
    isHydrationCurrent,
  } = deps;

  pendingHydrationRef.current = { conversationId, intentToken };
  // Clear the pending record only while it still describes THIS read — a
  // newer run may have overwritten it, and erasing that would drop ITS adopt.
  const settlePending = () => {
    const pending = pendingHydrationRef.current;
    if (pending && pending.conversationId === conversationId && pending.intentToken === intentToken) {
      pendingHydrationRef.current = null;
    }
  };

  let cancelled = false;
  void (async () => {
    const persistedState = await hydrateModulePaneState(conversationId);
    // Cancellation keeps the pending record: the cleanup that set `cancelled`
    // is immediately followed by a re-run that adopts this read (Guard 1).
    if (cancelled) return;
    if (hydrationSequence !== modulePaneHydrationSequenceRef.current) { settlePending(); return; }
    if (!isHydrationCurrent(intentToken)) { settlePending(); return; }
    if (!persistedState) { settlePending(); return; }
    settlePending();

    // Pure content swap: an explicit chat-only URL (view:'chat', set by our
    // conversation-switch nav) must NOT reopen a persisted module over the
    // chat-only view the user chose. Bare or /mod/X URLs (first load, reload,
    // module switch) still restore, preserving the intended auto-open.
    if (view === 'chat') return;

    // Guard 3 — an explicit module in the URL outranks persisted state: the
    // user (or a deep link) just asked for THAT module. Persisted state may
    // only contribute the anchor when it describes the same module.
    if (urlSeededModuleId) {
      if (persistedState.moduleId === urlSeededModuleId && persistedState.isVisible) {
        const restoredAnchorMessageId = persistedState.anchorMessageId
          ?? moduleEmbedAnchorByConversationRef.current[conversationId]
          ?? latestMessageIdRef.current;
        setModuleEmbedAnchorMessageId(restoredAnchorMessageId);
        moduleEmbedAnchorByConversationRef.current[conversationId] = restoredAnchorMessageId;
      }
      return;
    }

    setChatPaneModuleId(persistedState.moduleId);
    // On mobile, don't auto-restore module pane — it covers the chat and feels intrusive.
    // Only open when explicitly triggered by agent (module.focus action).
    const shouldRestore = isMobile ? false : Boolean(persistedState.isVisible && persistedState.moduleId);
    setIsModulePaneVisible(shouldRestore);
    const restoredAnchorMessageId = persistedState.anchorMessageId
      ?? moduleEmbedAnchorByConversationRef.current[conversationId]
      ?? latestMessageIdRef.current;
    setModuleEmbedAnchorMessageId(
      persistedState.isVisible && persistedState.moduleId
        ? restoredAnchorMessageId
        : null,
    );
    if (persistedState.isVisible && persistedState.moduleId) {
      moduleEmbedAnchorByConversationRef.current[conversationId] = restoredAnchorMessageId;
    }
  })();

  return () => {
    cancelled = true;
  };
}

export function runModulePaneHydrationEffect(deps: ModulePaneHydrationDeps): () => void {
  const {
    conversationId,
    isMobile,
    moduleIdParam,
    moduleEmbedAnchorMessageId,
    previousConversationIdRef,
    moduleEmbedAnchorByConversationRef,
    modulePaneHydrationSequenceRef,
    pendingHydrationRef,
    setChatPaneModuleId,
    setIsModulePaneVisible,
    setMobileView,
    setModuleEmbedAnchorMessageId,
    beginHydration,
  } = deps;

  const previousConversationId = previousConversationIdRef.current;

  // Guard 1 — same-conversation re-run (token rotation, dep identity churn,
  // StrictMode remount): never re-seed (the user may have moved the pane off
  // the URL's module). If the previous run's read was cancelled before it
  // applied, ADOPT it: re-issue the read under the ORIGINAL intent token so a
  // click made since the original run began still wins.
  if (conversationId && previousConversationId === conversationId) {
    const pending = pendingHydrationRef.current;
    if (pending && pending.conversationId === conversationId) {
      const urlSeededModuleId = isMobile ? null : (moduleIdParam ?? null);
      return startHydrationRead(
        deps,
        conversationId,
        urlSeededModuleId,
        modulePaneHydrationSequenceRef.current,
        pending.intentToken,
      );
    }
    return () => {
      /* hydration already settled for this conversation */
    };
  }

  if (previousConversationId && moduleEmbedAnchorMessageId) {
    moduleEmbedAnchorByConversationRef.current[previousConversationId] = moduleEmbedAnchorMessageId;
  }
  previousConversationIdRef.current = conversationId;

  const cachedAnchorForConversation = conversationId
    ? (moduleEmbedAnchorByConversationRef.current[conversationId] ?? null)
    : null;
  modulePaneHydrationSequenceRef.current += 1;
  const hydrationSequence = modulePaneHydrationSequenceRef.current;

  if (!conversationId) {
    setChatPaneModuleId(null);
    setIsModulePaneVisible(false);
    setMobileView('chat');
    setModuleEmbedAnchorMessageId(null);
    return () => {
      /* no async work scheduled */
    };
  }

  // Seed from URL instead of blanking so the pane stays rendered on the
  // conversation-change commit (the async hydration below still runs to
  // populate secondary state like anchorMessageId / fullscreen).
  const urlSeededModuleId = isMobile ? null : (moduleIdParam ?? null);
  setChatPaneModuleId(urlSeededModuleId);
  setIsModulePaneVisible(Boolean(urlSeededModuleId));
  setMobileView('chat');
  setModuleEmbedAnchorMessageId(cachedAnchorForConversation);

  // Guard 2 — capture the intent sequence BEFORE the async read; any
  // user/agent pane write after this point discards the result.
  const intentToken = beginHydration();

  return startHydrationRead(deps, conversationId, urlSeededModuleId, hydrationSequence, intentToken);
}
